import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import CartDrawer from "../../common/cartpage/CartDrawer";
import { ToastContainer, toast } from "react-toastify";
import { useMenu } from "../../context/MenuContext";
import "react-toastify/dist/ReactToastify.css";
import CurrencySelector from "./priceCUrrency";
import ProductSkeleton from "../../skeletons/ProductSkeleton";
import ChildCategoryProducts from "../mens/mendropdown/ChildCategoryProducts";
import Noproduct from "../../../assets/common/no-product.webp";
import { FaStar } from "react-icons/fa";
import PincodeChecker from "./PincodeChecker";
import axios from "axios";
import Swal from "sweetalert2";
import { ChevronLeft, ChevronRight, Eye, X, Heart } from "lucide-react";

const ProductPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [tab, setTab] = useState("description");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const { fetchMenuCounts, setCartCount } = useMenu(); // ✅ added setCartCount
  const [addingToCart, setAddingToCart] = useState(false);
  const [zoomStyle, setZoomStyle] = useState({});
  const [isZooming, setIsZooming] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0); // decimal ratings like 3.5
  const [hoverRating, setHoverRating] = useState(0);
  const [errors, setErrors] = useState({});
  const [avgRating, setAvgRating] = useState(0);
  const [comment, setComment] = useState("");
  const storedUser = JSON.parse(localStorage.getItem("userData") || "null");

  const [guestName, setGuestName] = useState(storedUser?.name || "");
  const [guestEmail, setGuestEmail] = useState(storedUser?.email || "");

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const { slug } = useParams();
  const user = JSON.parse(localStorage.getItem("authUserId"));
  const id = user;
  const token =
    localStorage.getItem("authToken") || localStorage.getItem("guest_token");

  // ✅ Get currently selected variant
  const getSelectedVariant = () => {
    return product?.variants?.find(
      (v) => v.color_id === selectedColor && v.size_id === selectedSize,
    );
  };

  // ✅ Fetch product
  useEffect(() => {
    window.scrollTo(0, 0); // 👈 yahi add karna hai
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `https://tyka.premierhostings.com/beta/api/site-products/${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const data = await res.json();
        const prod = data.data;

        setProduct({
          ...prod,
          reviews: prod.Review || [],
        });

        if (prod?.variants?.length > 0) {
          const defaultVariant =
            prod.variants.find((v) => v.status === 1) || prod.variants[0];

          setSelectedColor(defaultVariant.color_id);
          setSelectedSize(defaultVariant.size_id || "");
          setSelectedCurrency(defaultVariant.prices || "");

          const defaultImage =
            defaultVariant.variant_images?.find((img) => img.is_default)
              ?.path ||
            defaultVariant.variant_images?.[0]?.path ||
            prod.default_image ||
            "";

          setMainImage(defaultImage);
        } else {
          setMainImage(prod.default_image || "");
        }
        // ✅ average rating calculate
        if (prod.Review && prod.Review.length > 0) {
          const total = prod.Review.reduce(
            (sum, r) => sum + Number(r.rating_value || 0),
            0,
          );
          setAvgRating(total / prod.Review.length);
        } else {
          setAvgRating(0);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  if (loading) return <ProductSkeleton />;
  if (!product) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="text-center">
          <img
            src={Noproduct} // or ProductNotFound image
            alt="Product Not Found"
            className="w-full max-w-md mx-auto"
          />

          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Product Not Found
          </h2>

          <p className="mt-2 text-gray-500">
            Sorry, the product you're looking for doesn't exist or has been
            removed.
          </p>

          <button
            onClick={() => navigate("/products")}
            className="mt-6 rounded-lg bg-black px-6 py-3 text-white transition hover:bg-red-600"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // ✅ Price calculation
  const basePrice = parseFloat(product?.cost_price) || 0;
  const sellingPrice = parseFloat(product?.selling_price) || 0;
  const discountType = product?.discount;
  const hasDiscount = sellingPrice > 0 && sellingPrice < basePrice;
  const totalPrice = (
    quantity * (hasDiscount ? sellingPrice : basePrice)
  ).toFixed(2);

  const currentVariant = getSelectedVariant();
  const variantImages = currentVariant?.variant_images || [];
  const maxStock = currentVariant?.stock || 0;

  // ✅ Add to cart (Guest + Logged-in)
  const handleAddToCart = async (type = "product", bundle = null) => {
    if (type === "product") {
      if (!product?.id) return;

      if (!selectedColor || !selectedSize) {
        toast.warning("Please select color and size.");
        return;
      }
    }

    const payload =
      type === "bundle"
        ? {
            item_type: "bundle",
            bundle_id: bundle.id,
            quantity,
          }
        : {
            item_type: "product",
            product_id: product.id,
            color_id: selectedColor,
            size_id: selectedSize,
            quantity,
          };

    setAddingToCart(true);

    try {
      const authToken = localStorage.getItem("authToken");
      let guestToken = localStorage.getItem("guest_token");
      let url = `https://tyka.premierhostings.com/beta/api/cart`;
      let headers = { "Content-Type": "application/json" };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      } else if (guestToken) {
        url += `?guest_token=${guestToken}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        if (!authToken && data.guest_token) {
          localStorage.setItem("guest_token", data.guest_token);
        }

        if (!authToken) {
          setCartCount((prev) => prev + 1); // ✅ instant update for guest
        } else {
          fetchMenuCounts(id, token); // ✅ logged-in user update via API
        }

        setCartOpen(true);

        toast.success(data.message || "Product added to cart!", {
          position: "top-right",
          autoClose: 700,
          hideProgressBar: true,
          pauseOnHover: false,
          draggable: false,
          closeOnClick: true,
        });
      } else {
        toast.error(data.message || "Failed to add to cart.");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddToWishlist = async () => {
    console.log("dfgdfdf");
  };

  // ✅ Zoom Handlers

  const handleSave = async () => {
    let newErrors = {};

    // Get logged-in user
    const storedUser = JSON.parse(localStorage.getItem("authUser") || "null");

    // Validate guest fields only when user is not logged in
    if (!storedUser) {
      if (!guestName.trim()) {
        newErrors.guestName = "This field is required";
      }

      if (!guestEmail.trim()) {
        newErrors.guestEmail = "This field is required";
      }
    }

    // Rating validation
    if (rating === 0) {
      newErrors.rating = "At least 1 rating required";
    }

    // Show validation errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    if (!product?.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select a product first",
      });
      return;
    }

    try {
      const response = await axios.post(
        "https://tyka.premierhostings.com/beta/api/product-reviews",
        {
          product_id: product.id,
          rating_value: rating,
          rating_title: "Rating",
          rating_desc: comment,

          // User data from localStorage
          user_id: storedUser?.id || null,
          guest_name: storedUser?.name || guestName,
          guest_email: storedUser?.email || guestEmail,

          is_active: 1,
        },
      );

      console.log("Review Response:", response.data);

      // Check API success
      if (response?.data?.success === true && response?.data?.status === 200) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.data?.message || "Review submitted successfully!",
        });

        setShowModal(false);
        setRating(0);
        setComment("");
        setErrors({});

        // Only clear guest fields
        if (!storedUser) {
          setGuestName("");
          setGuestEmail("");
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.data?.message || "Failed to submit review.",
        });
      }
    } catch (error) {
      console.error("Error submitting review:", error?.response?.data || error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
      });
    }
  };

  const handleMouseEnter = () => {
    setIsZooming(true);
    setZoomStyle({
      backgroundSize: "200%",
      backgroundPosition: "center",
    });
  };

  const handleMouseMove = (e) => {
    if (!isZooming) return;
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - top) / height) * 100;
    setZoomStyle({
      backgroundSize: "200%",
      backgroundPosition: `${x}% ${y}%`,
    });
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
    setZoomStyle({
      backgroundSize: "contain",
      backgroundPosition: "center",
    });
  };
  const openPopup = (index) => {
    setCurrentIndex(index);
    setIsPopupOpen(true);
  };

  const closePopup = () => setIsPopupOpen(false);
  const prevImage = () =>
    setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));

  const nextImage = () =>
    setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));

  const allImages = [mainImage, ...variantImages.map((img) => img.path)];
  const selectedVariant = product?.variants?.find(
    (variant) =>
      String(variant.color_id) === String(selectedColor) &&
      String(variant.size_id) === String(selectedSize),
  );
  return (
    <div className="product-page max-w-6xl mx-auto px-4 py-8">
      <ToastContainer />

      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 flex-wrap">
        <button
          onClick={() => navigate("/")}
          className="hover:text-[#FF0009] transition"
        >
          Home
        </button>

        <span>/</span>

        <button
          onClick={() =>
            navigate(`/head-category/${product?.head_categories?.[0]?.slug}`)
          }
          className="hover:text-[#FF0009] transition"
        >
          {product?.head_categories?.[0]?.name || "Category"}
        </button>

        <span>/</span>

        <span className="text-gray-900 font-medium capitalize">
          {product?.name?.toLowerCase()}
        </span>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left - Image Zoom */}
        <div className="w-full lg:w-[40%]">
          {/* Main Image Box */}
          <div
            className="w-full h-[400px] sm:h-[500px] lg:h-[600px] flex items-center justify-center overflow-hidden relative group "
            style={{
              backgroundImage: `url(${mainImage})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: isZooming ? "250%" : "contain",
              backgroundPosition: zoomStyle.backgroundPosition || "center",
              cursor: isZooming ? "zoom-in" : "default",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <img
              src={mainImage}
              alt="product"
              className="max-h-full max-w-full object-contain opacity-0"
            />

            {/* Eye Icon */}
            <button
              onClick={() => openPopup(0)}
              className="absolute top-3 right-3 bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <Eye size={20} />
            </button>
          </div>

          {/* Thumbnails */}
          <div className="flex gap-3 mt-4 flex-wrap">
            {variantImages.map((img, idx) => (
              <div
                key={idx}
                className={`w-20 h-20 rounded-md overflow-hidden border cursor-pointer ${
                  mainImage === img.path ? "border-black" : "border-gray-300"
                }`}
                onClick={() => setMainImage(img.path)}
              >
                <img
                  src={img.path}
                  alt={`thumb-${idx}`}
                  className="w-full h-full object-contain"
                />
              </div>
            ))}
          </div>

          {/* Popup Modal */}
          {isPopupOpen && (
            <div
              className="fixed inset-0 bg-white backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={closePopup} // overlay click → close
            >
              {/* Image Wrapper */}
              <div
                className="relative w-full max-w-4xl flex items-center justify-center"
                onClick={(e) => e.stopPropagation()} // image पर click करने से popup बंद न हो
              >
                {/* Current Image */}
                <img
                  src={allImages[currentIndex]}
                  alt="popup"
                  className="max-h-[80vh] max-w-full object-contain rounded-lg"
                />

                {/* Close Button (image ke upar right top) */}
                <button
                  onClick={closePopup}
                  className="absolute top-2 right-2 text-white bg-black/50 p-2 rounded-full hover:bg-black/70"
                >
                  <X size={22} />
                </button>

                {/* Left Arrow (image ke bilkul left side par) */}
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-black/50 p-2 sm:p-3 rounded-full hover:bg-black/70"
                >
                  <ChevronLeft size={22} />
                </button>

                {/* Right Arrow (image ke bilkul right side par) */}
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-black/50 p-2 sm:p-3 rounded-full hover:bg-black/70"
                >
                  <ChevronRight size={22} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right - Details */}
        <div className="w-full lg:w-1/2 ">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-2xl font-semibold">{product?.name}</h2>

            {/* <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddToWishlist(e, product);
              }}
              className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 hover:border-[#FF0009] hover:bg-red-50 transition-all duration-200 group"
            >
              <Heart
                size={22}
                className="text-gray-600 group-hover:text-[#FF0009]"
              />
            </button> */}
          </div>
          <p className="text-gray-500 mt-1 flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, starIdx) => (
              <FaStar
                key={starIdx}
                className={`${
                  starIdx < Math.round(product?.rating_counts || 0)
                    ? "text-yellow-400"
                    : "text-gray-300"
                }`}
              />
            ))}
            <span className="ml-1">
              ({product?.rating_counts || 0} Reviews)
            </span>
          </p>

          {selectedCurrency.length > 0 ? (
            <CurrencySelector selectedCurrency={selectedCurrency} />
          ) : hasDiscount ? (
            <div className="flex items-center gap-3 mt-2">
              <span className="text-3xl font-bold text-black">
                ₹{totalPrice}
              </span>
              <span className="text-lg text-gray-500 line-through">
                ₹{basePrice}
              </span>
              <p className="text-sm text-green-600">
                {discountType?.type === "percentage"
                  ? `${Math.round(((basePrice - totalPrice) / basePrice) * 100)}% OFF`
                  : discountType?.type === "fixed"
                    ? `₹${parseInt(discountType.amount, 10)} OFF`
                    : ""}
              </p>
            </div>
          ) : (
            <p className="text-3xl font-bold text-black mt-2">₹{totalPrice}</p>
          )}
          <p className="text-sm text-gray-500">Inclusive of all taxes</p>

          <div
            className="mt-3 text-gray-700 text-lg leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: product?.short_description || "",
            }}
          />

          {/* Colors */}
          {product?.variants?.length > 0 && (
            <div className="mt-4">
              Color:{" "}
              <strong>
                {
                  product?.variants.find((v) => v.color_id === selectedColor)
                    ?.color_name
                }
              </strong>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  ...new Map(
                    product.variants.map((v) => [v.color_id, v]),
                  ).values(),
                ].map((color) => (
                  <button
                    key={color.color_id}
                    className={`w-8 h-8 rounded-full border transition-all duration-150 ${
                      selectedColor === color.color_id
                        ? "ring-2 ring-black"
                        : "border-black"
                    }`}
                    title={color.color_name}
                    style={{
                      background: color.color_code_2
                        ? `linear-gradient(135deg, ${color.color_code} 50%, ${color.color_code_2} 50%)`
                        : color.color_code || "#ccc",
                    }}
                    onClick={() => {
                      setSelectedColor(color.color_id);
                      const sizesForColor = product.variants.filter(
                        (v) => v.color_id === color.color_id,
                      );
                      if (sizesForColor.length > 0) {
                        setSelectedSize(sizesForColor[0].size_id);
                        const defImg =
                          sizesForColor[0].variant_images.find(
                            (i) => i.is_default,
                          )?.path ||
                          sizesForColor[0].variant_images[0]?.path ||
                          product.default_image;
                        setMainImage(defImg);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {product?.variants?.length > 0 && (
            <div className="mt-4">
              Size:{" "}
              <strong>
                {
                  product?.variants.find((v) => v.size_id === selectedSize)
                    ?.size_name
                }
              </strong>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  ...new Map(
                    product.variants
                      .filter((v) => v.color_id === selectedColor)
                      .map((v) => [v.size_id, v]),
                  ).values(),
                ].map((size, idx) => (
                  <button
                    key={idx}
                    className={`px-3 py-1 border rounded ${
                      selectedSize === size.size_id
                        ? "border-black font-semibold"
                        : "border-gray-400"
                    }`}
                    title={size.size_name}
                    onClick={() => {
                      setSelectedSize(size.size_id);
                      const defImg =
                        size.variant_images.find((i) => i.is_default)?.path ||
                        size.variant_images[0]?.path ||
                        product.default_image;
                      setMainImage(defImg);
                    }}
                  >
                    {size.size_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + Cart */}
          <div className="flex items-center gap-3 mt-6">
            <strong>Qty:</strong>
            <button
              className="px-2 border rounded"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            >
              -
            </button>
            <span>{quantity}</span>
            <button
              className="px-2 border rounded disabled:opacity-50"
              onClick={() => setQuantity((q) => q + 1)}
              disabled={quantity >= maxStock}
            >
              +
            </button>
            <span
              className={`ml-3 ${
                currentVariant?.stock > 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {currentVariant?.stock > 0 ? `In stock` : "Out of stock"}
            </span>
            <button
              onClick={() => handleAddToCart("product")}
              className="bg-black text-white px-5 py-2 rounded font-semibold disabled:opacity-50"
              disabled={
                !currentVariant || currentVariant.stock <= 0 || addingToCart
              }
            >
              {addingToCart ? "ADDING..." : "ADD TO CART"}
            </button>
          </div>

          {/* Info */}
          <div className="mt-4 text-sm text-gray-600 space-y-2">
            <p>
              <span className="text-gray-500">SKU:</span>{" "}
              <strong className="text-black">
                <span>{product?.sku?.toUpperCase() || "N/A"}</span>
              </strong>
            </p>
            <p>
              <span className="text-gray-500">Categories:</span>{" "}
              <strong className="text-black">
                {(product?.head_categories || [])
                  .concat(product?.parent_categories || [])
                  .concat(product?.child_categories || [])
                  .map((c, i, arr) => (
                    <span key={i}>
                      <Link className="hover:underline hover:text-blue-600">
                        {c.name.charAt(0).toUpperCase() + c.name.slice(1)}
                      </Link>
                      {i < arr.length - 1 ? ", " : ""}
                    </span>
                  ))}
              </strong>
            </p>
            <p>
              <span className="text-gray-500">Tags:</span>{" "}
              <strong className="text-black">
                {(product?.tags || []).map((t, i) => (
                  <span key={i}>
                    <span className="hover:underline hover:text-blue-600">
                      {t.name.charAt(0).toUpperCase() + t.name.slice(1)}
                    </span>
                    {i < product.tags.length - 1 ? ", " : ""}
                  </span>
                ))}
              </strong>
            </p>
          </div>
          <div className="mt-4 text-sm text-gray-600 space-y-2">
            <PincodeChecker />
          </div>
        </div>
      </div>

      {product?.bundles?.map((bundle) => {
        const allProducts = bundle?.products || [];

        return (
          <div
            key={bundle.id}
            className="mt-10 border rounded-xl p-4 md:p-6 bg-white shadow-sm"
          >
            <h2 className="text-xl md:text-2xl font-bold mb-5">
              Frequently Bought Together
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
              {/* PRODUCTS SECTION */}
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[...allProducts].reverse().map((item, index) => {
                    const displayVariant = item?.variant
                      ? {
                          sizeName: item?.variant?.size?.name,
                          colorName: item?.variant?.color?.name,
                          colorCode: item?.variant?.color?.code,
                        }
                      : null;
                    const itemPrice = item?.selling_price ?? item?.price ?? 0;

                    return (
                      <div
                        key={item.id || index}
                        className="flex items-center justify-center gap-5"
                      >
                        {/* Product */}
                        <div className="flex flex-col items-center p-1 w-full">
                          <img
                            src={item.default_image}
                            alt={item.name || "Product"}
                            className="w-40 h-40 object-contain"
                          />

                          <p className="mt-3 text-center text-sm md:text-base font-medium">
                            {item.name}
                          </p>

                          {/* SIZE */}
                          {displayVariant?.sizeName && (
                            <p className="mt-1 text-xs md:text-sm text-gray-600">
                              <span className="font-semibold text-gray-800">
                                Size:
                              </span>{" "}
                              {displayVariant.sizeName}
                            </p>
                          )}

                          {/* COLOR */}
                          {displayVariant?.colorName && (
                            <div className="mt-1 flex items-center justify-center gap-2 text-xs md:text-sm text-gray-600">
                              <span className="font-semibold text-gray-800">
                                Color:
                              </span>

                              <span
                                className="w-4 h-4 rounded-full border border-gray-300"
                                style={{
                                  backgroundColor:
                                    displayVariant.colorCode || "#ccc",
                                }}
                                title={displayVariant.colorName}
                              />

                              <span>{displayVariant.colorName}</span>
                            </div>
                          )}

                          {/* PRICE */}
                          <p className="text-[#FF0009] font-semibold mt-1">
                            ₹ {Number(itemPrice).toFixed(2)}
                          </p>
                        </div>

                        {/* PLUS */}
                        {index !== allProducts.length - 1 && (
                          <span className="text-3xl font-bold">+</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* SUMMARY */}
              <div className="w-full lg:w-80 border-2 rounded-lg p-5 bg-gray-50">
                <h3 className="text-lg md:text-xl font-bold">{bundle.name}</h3>

                <p className="text-gray-500 text-sm mb-4">
                  {bundle.description}
                </p>

                <div className="space-y-2">
                  {Number(bundle.original_price) > 0 && (
                    <div className="flex justify-between">
                      <span>Original Price</span>

                      <span
                        className={
                          Number(bundle.discount) > 0 ? "line-through" : ""
                        }
                      >
                        ₹{bundle.original_price}
                      </span>
                    </div>
                  )}

                  {Number(bundle.offer_price) > 0 && (
                    <div className="flex justify-between text-green-600 font-bold">
                      <span>Bundle Price</span>

                      <span>₹{bundle.offer_price}</span>
                    </div>
                  )}

                  {Number(bundle.discount) > 0 && (
                    <div className="flex justify-between text-red-500">
                      <span>Discount</span>

                      <span>{Math.round(Number(bundle.discount))}% OFF</span>
                    </div>
                  )}

                  {Number(bundle.original_price) - Number(bundle.offer_price) >
                    0 && (
                    <div className="flex justify-between text-green-600 font-semibold">
                      <span>You Save</span>

                      <span>
                        ₹
                        {(
                          Number(bundle.original_price) -
                          Number(bundle.offer_price)
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleAddToCart("bundle", bundle)}
                  className="
      w-full
      mt-5
      bg-yellow-400
      hover:bg-yellow-500
      rounded-full
      py-3
      font-semibold
    "
                >
                  Add Bundle To Cart
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Tabs */}
      <div className="mt-12">
        <div className="flex gap-6 border-b">
          {["description", "reviews"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-2 ${
                tab === t
                  ? "border-b-2 border-black font-semibold"
                  : "text-gray-500"
              }`}
            >
              {t === "description" ? "Description" : "Reviews"}
            </button>
          ))}
        </div>
        <div className="mt-4">
          {tab === "description" && (
            <div
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: product?.description || "" }}
            />
          )}
          {/* {tab === "info" && (
            <p>
              Material: {product?.fabric?.name || "N/A"} | Fit: Regular |
              Washable: Yes
            </p>
          )} */}
          {tab === "reviews" && (
            <div>
              <div className="mb-4">
                {product?.reviews?.length > 0 ? (
                  product.reviews.map((r, idx) => (
                    <div key={idx} className="border-b py-2">
                      <div className="flex items-center gap-2">
                        {/* ⭐ rating show */}
                        {Array.from({ length: 5 }).map((_, starIdx) => (
                          <FaStar
                            key={starIdx}
                            className={`${
                              starIdx < r.rating_value
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                      {/* <p className="font-semibold">{r.rating_title}</p> */}
                      <p className="text-gray-600">{r.rating_desc}</p>
                    </div>
                  ))
                ) : (
                  <p>No reviews yet. Be the first to review!</p>
                )}
              </div>

              {/* Add Review Button */}
              <button
                onClick={() => setShowModal(true)}
                className="px-6 py-2 bg-[rgb(255,0,25)] border text-white hover:bg-[rgb(200,0,20)] hover:text-white transition-colors rounded-full text-md font-bold"
              >
                Add Review
              </button>

              {/* Modal */}
              {showModal && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 sm:px-0"
                  onClick={() => setShowModal(false)} // click outside closes modal
                >
                  <div
                    className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-md p-6 relative"
                    onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
                  >
                    <h2 className="text-xl font-bold mb-4 text-center">
                      Write a Review
                    </h2>

                    {!storedUser?.name && (
                      <>
                        {/* Guest Name */}
                        <div className="mb-4">
                          <input
                            type="text"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            placeholder="Your Name"
                            className={`w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                              errors.guestName ? "border-red-500" : ""
                            }`}
                          />

                          {errors.guestName && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.guestName}
                            </p>
                          )}
                        </div>

                        {/* Guest Email */}
                        <div className="mb-4">
                          <input
                            type="email"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            placeholder="Your Email"
                            className={`w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                              errors.guestEmail ? "border-red-500" : ""
                            }`}
                          />

                          {errors.guestEmail && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.guestEmail}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    {/* Star Rating */}
                    <div className="flex flex-col items-center justify-center mb-4">
                      <div className="flex justify-center">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const fill =
                            star <= (hoverRating || rating)
                              ? "text-yellow-400"
                              : "text-gray-300";

                          return (
                            <FaStar
                              key={star}
                              className={`cursor-pointer ${fill} text-3xl`}
                              onClick={() => setRating(star)}
                              onMouseEnter={() => setHoverRating(star)}
                              onMouseLeave={() => setHoverRating(0)}
                            />
                          );
                        })}
                      </div>

                      {errors.rating && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.rating}
                        </p>
                      )}
                    </div>

                    {/* Comment Box */}
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Write your review..."
                      className="w-full border rounded p-2 mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
                    />

                    {/* Save Button */}
                    <div className="flex flex-col sm:flex-row justify-between gap-2">
                      <button
                        onClick={() => setShowModal(false)}
                        className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-full w-full sm:w-auto"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className="px-6 py-2 bg-[rgb(255,0,25)] border text-white hover:bg-[rgb(200,0,20)] hover:text-white transition-colors rounded-full text-md font-bold"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {product.related_products?.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4">Related Products</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {product.related_products.map((item) => {
              const originalPrice = Number(item.price || item.cost_price || 0);
              const sellingPrice = Number(
                item.selling_price || item.price || 0,
              );

              const discount =
                originalPrice > sellingPrice
                  ? Math.round(
                      ((originalPrice - sellingPrice) / originalPrice) * 100,
                    )
                  : 0;

              return (
                <div
                  key={item.id}
                  className="border rounded-lg p-3 hover:shadow-md cursor-pointer"
                >
                  <img
                    src={item.default_image}
                    alt={item.name}
                    className="w-full h-56 object-cover"
                  />

                  <h4 className="mt-2 font-medium">{item.name}</h4>

                  <div className="mt-2">
                    {discount > 0 ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-red-600 font-bold text-lg">
                          ₹{sellingPrice}
                        </span>

                        <span className="text-gray-500 line-through">
                          ₹{originalPrice}
                        </span>

                        <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-semibold">
                          {discount}% OFF
                        </span>
                      </div>
                    ) : (
                      <span className="text-red-600 font-bold text-lg">
                        ₹{sellingPrice || originalPrice}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cart Drawer */}
      {cartOpen && <CartDrawer open={cartOpen} setOpen={setCartOpen} />}
    </div>
  );
};

export default ProductPage;
